import { useQuery } from '@tanstack/react-query';
import { melaApi } from '../api/modules';
import { Drawer, DrawerCloseButton } from '../components/ui/Drawer';
import { StatusPill } from '../components/ui/StatusPill';
import { useToast } from '../components/toast/Toast';

interface Props {
  open: boolean;
  code: string | null;
  onClose: () => void;
}

export function MelaCampDrawer({ open, code, onClose }: Props) {
  const { show } = useToast();
  const { data, isLoading } = useQuery({
    enabled: !!code,
    queryKey: ['mela-camp', code],
    queryFn: () => melaApi.detail(code!),
  });

  const dateLabel = data
    ? new Date(data.date).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
    : '';

  const completion = data && data.actual !== null && data.target > 0
    ? Math.min(100, Math.round((data.actual / data.target) * 100))
    : null;

  return (
    <Drawer open={open && !!code} onClose={onClose}>
      <div className="bg-navy-800 text-white px-6 py-5 flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest opacity-80">Pashu Arogyasheel Mela · {code}</div>
          <h2 className="serif-display text-2xl font-medium mt-1">{data ? dateLabel : 'Loading…'}</h2>
          {data && (
            <div className="text-sm opacity-90 mt-2">
              {data.location.np ?? data.location.district} · {data.location.mandal} mandal
            </div>
          )}
        </div>
        <DrawerCloseButton onClick={onClose} />
      </div>

      <div className="px-6 py-5 flex-1 overflow-y-auto space-y-5">
        {isLoading || !data ? (
          <div className="space-y-3">
            <div className="h-20 bg-cream-100 rounded-sm animate-pulse" />
            <div className="h-32 bg-cream-100 rounded-sm animate-pulse" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Status</div>
                <div className="mt-1"><StatusPill status={data.status} /></div>
              </div>
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Target</div>
                <div className="font-numeric serif-display text-xl text-navy-800 font-medium mt-1">
                  {data.target.toLocaleString('en-IN')}
                </div>
              </div>
              <div className={`p-3 rounded-sm ${data.actual !== null && data.actual >= data.target ? 'bg-green-50' : 'bg-cream-50'}`}>
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Actual</div>
                <div className={`font-numeric serif-display text-xl font-medium mt-1 ${
                  data.actual === null ? 'text-navy-800/40' :
                  data.actual >= data.target ? 'text-green-700' : 'text-red-600'
                }`}>
                  {data.actual !== null ? data.actual.toLocaleString('en-IN') : 'TBD'}
                </div>
                {completion !== null && (
                  <div className="text-[11px] text-navy-800/60 mt-1 font-numeric">{completion}% of target</div>
                )}
              </div>
            </div>

            {completion !== null && (
              <div>
                <div className="h-2 bg-cream-100 rounded-sm overflow-hidden">
                  <div
                    className={completion >= 100 ? 'h-full bg-green-600' : completion >= 70 ? 'h-full bg-saffron-500' : 'h-full bg-red-500'}
                    style={{ width: `${Math.min(100, completion)}%` }}
                  />
                </div>
              </div>
            )}

            <div className="bg-cream-50 p-3 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Lead officer</div>
              <div className="text-sm font-medium text-navy-800 mt-1">{data.lead.name}</div>
            </div>

            {data.services_planned && data.services_planned.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-widest text-navy-800/50 mb-2 font-semibold">Services planned</h3>
                <div className="flex flex-wrap gap-2">
                  {data.services_planned.map((s) => (
                    <span key={s} className="text-xs bg-navy-100 text-navy-800 px-3 py-1 rounded-sm capitalize">
                      {s.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {data.notes && (
              <div>
                <h3 className="text-xs uppercase tracking-widest text-navy-800/50 mb-2 font-semibold">Notes</h3>
                <p className="text-sm text-navy-800 bg-cream-50 p-3 rounded-sm">{data.notes}</p>
              </div>
            )}

            <div>
              <h3 className="text-xs uppercase tracking-widest text-navy-800/50 mb-2 font-semibold">Coming in Day 7</h3>
              <ul className="text-xs text-navy-800/70 space-y-1.5 pl-4 list-disc">
                <li>Per-camp beneficiary capture log</li>
                <li>Disease surveillance reports filed during the camp</li>
                <li>Site photographs &amp; geo-tagged check-ins</li>
                <li>Service-by-service outcome counters</li>
              </ul>
            </div>
          </>
        )}
      </div>

      <div className="border-t border-cream-200 px-6 py-4 flex items-center gap-2 bg-cream-50">
        <button
          type="button"
          onClick={() => show('Camp summary report queued — will be emailed')}
          className="flex-1 bg-navy-800 hover:bg-navy-700 text-white text-sm py-2.5 rounded-sm"
        >
          Export camp summary
        </button>
        {data && data.status === 'scheduled' && (
          <button
            type="button"
            onClick={() => show('Mela calendar published · Field teams notified')}
            className="px-4 py-2.5 border border-saffron-500 text-saffron-600 hover:bg-saffron-50 text-sm rounded-sm"
          >
            Publish to calendar
          </button>
        )}
      </div>
    </Drawer>
  );
}
